<template>
  <span class="jps-query-span" :title="getSpanTitle">
    <span v-for="(element, i) in elements" :key="i" :class="element.tagClass">
      <b v-if="element.strongLabel">{{ element.strongLabel}}</b>
      <template v-if="element.label">{{ element.label }}</template>
    </span>
  </span>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'

interface Props {
  q: SearchQuery
  cs?: object
  hideCs?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  cs: null,
  hideCs: false
})

const { ls } = useI18n()
const dataStore = useDataStore()
const { $getCodeRdfType } = useNuxtApp()

const rdfTypeCode: Code = $getCodeRdfType()

interface ElementInfo {
  tag: string
  tagClass: string
  label: string
  strongLabel: string
}
const elements: Ref<ElementInfo[]> = ref([])
const text: Ref<string[]> = ref([])

const getSpanTitle = computed(() => {
  return text.value.join(' ')
})

onMounted(() => {
  init()
})

const init = () => {
  let cs: any = {}
  if (props.cs) {
    cs = props.cs
  } else if (props.q.csid) {
    cs = dataStore.csearch(props.q.csid)
  }

  if (!props.hideCs && cs && cs.name) {
    let head = ls(cs.name)
    elements.value.push({
      tag: 'span',
      tagClass: 'jps-q-cs',
      label: head,
      strongLabel: null })
    text.value.push(head)
  }

  let fullSearch = true
  if (!isEmpty(props.q.keyword)) {
    let head = props.q.keyword.join(' ')
    elements.value.push({
      tag: 'span',
      tagClass: 'jps-q-keyword',
      label: head,
      strongLabel: null})
    text.value.push(head)
    fullSearch = false
  }

  if (!isEmpty(props.q.query)) {
    processQuery(cs, props.q.query)
    fullSearch = false
  }

  if (!isEmpty(props.q.filter)) {
    processQuery(cs, props.q.filter)
    fullSearch = false
  }

  if (!isEmpty(props.q.geoDistance)) {
    let head = ls({ja: '座標検索', en: 'Geo Search'})
    elements.value.push({
      tag: 'span',
      tagClass: 'jps-q-field',
      label: null,
      strongLabel: head})
    text.value.push(head)
    fullSearch = false
  }

  if (!isEmpty(props.q.image)) {
    let head = ls(dapLangGlobal.imageSearch)
    let items = props.q.image
      .map((id) => {
        let item = dataStore.item(id)
        if (item) {
          return ls({ ja: item.common.title, en: item.common.titleEn })
        }
      })
      .join(',')
    elements.value.push({
      tag: 'span',
      tagClass: 'jps-q-field',
      label: ':' + items,
      strongLabel: head})
    text.value.push(head + ':' + items)
    fullSearch = false
  }

  if (props.q.text2image) {
    let head = ls(dapLangGlobal.imageSearch)
    elements.value.push({
      tag: 'span',
      tagClass: 'jps-q-field',
      label: ':' + props.q.text2image,
      strongLabel: head})
    text.value.push(head + ':' + props.q.text2image)
    fullSearch = false
  }

  if (fullSearch) {
    let head = ls({ja: '全件検索', en: 'Full Search'})
    elements.value.push({
      tag: 'span',
      tagClass: 'jps-q-fullsearch',
      label: null,
      strongLabel: head})
    text.value.push(head)
  }
}

const processQuery = (cs: CustomSearchDefinition, q: { [key: string]: string[] } ) => {
  Object.entries(q).map(([key, values]) => {
    if (cs && cs.searchFields) {
      let field = cs.searchFields.find((f) => f.key === key)
      if (field) {
        let queryValue = values.map((value) => {
          let vdet = value.replace(/^[+-]/, '')
          let prefex = value.startsWith('-') ? 'NOT ' : ''
          switch (field.type) {
            case 'rdf_type':
              let cv = rdfTypeCode.values.find((v) => v.code === vdet)
              if (cv) return prefex + ls(cv.value)
              break
            case 'code':
              let code = dataStore.code(field.codeKey)
              if (code) {
                let cv = code.values.find((v) => v.code === vdet)
                if (cv) return prefex + ls(cv.value)
              }
              break
            case 'database':
              let db = dataStore.database(vdet)
              if (db) return prefex + ls(db.name)
              break
          }
          return prefex + vdet
        })
        text.value.push(
          ls(field.name) + ':' + queryValue.join(',')
        )

        elements.value.push({
          tag: 'span',
          tagClass: 'jps-q-field',
          label: ':' + queryValue.join(','),
          strongLabel: ls(field.name)})
      }
      text.value.push(values.join(','))
      elements.value.push({
        tag: 'span',
        tagClass: null,
        label: values.join(','),
        strongLabel: null})
    }
  })
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-query-span {
  .jps-q-cs {
    font-weight: bold;
    padding-right: 0.2rem;
  }
  .jps-q-keyword {
    margin-right: 0.2rem;
  }
  .jps-q-field {
    b {
      font-weight: 500;
      color: $jps-key-green;
    }
    margin-right: 0.1rem;
  }
  .jps-q-fullsearch {
    color: $jps-key-blue;
  }
}
</style>
